<script setup lang="ts">
import List from '@/components/List.vue';
import { ActionListType } from '@/router/actionPoints';
import { useActionPointStore } from '@/stores/actionPoint';
import { useFilterStore } from '@/stores/components/filter';
import { getTotalCountFromChartData } from '@/utils/chart';
import { getCurrentMonthStartDate, getNextMonthStartDate } from '@/utils/date';
import { processErrorWithFeedback } from '@/utils/error';
import type { ChartData, ChartOptions, ChartType } from 'chart.js';
import { computed, inject, onMounted, ref, type ComputedRef } from 'vue';
import { Chart } from 'vue-chartjs';
import { useRouter, type RouteLocationRaw } from 'vue-router';
import { useDisplay } from 'vuetify';

const actionPointStore = useActionPointStore();
const filterStore = useFilterStore();
const router = useRouter();

const now = new Date();
const currentMonthStartDate = getCurrentMonthStartDate(now);
const nextMonthStartDate = getNextMonthStartDate(now);

const { smAndDown } = useDisplay();

const props = defineProps<{
    title: string;
    type: ChartType;
    itemType: string;
    isCompleted: boolean;
    emptyMessage: string;
    chartOptions?: ChartOptions;
    showMagnify?: boolean;
}>();

const organizationId = inject('organizationId') as ComputedRef<string>;

type CustomChartData = ChartData<ChartType, number[], string> & { ids: string[] };
const chartData = ref<CustomChartData>();
const isLoading = ref(true);

const options = {
    plugins: {
        datalabels: false,
        tooltip: {
            callbacks: {
                label: (tooltipItem: any) => {
                    const value = tooltipItem.raw;
                    const percentage = ((value / totalCount.value) * 100).toFixed(0);
                    return ` ${percentage}% (${value})`;
                }
            }
        }
    },
    scales: { x: { display: false } },
    interaction: {
        intersect: false // Ensure tooltips display, even on small graphs
    },
    responsive: true,
    maintainAspectRatio: true,
    color: 'grey',

    onClick: (_event: any, elements: any) => {
        if (elements[0]) {
            const i = elements[0].index;
            const clickedId = chartData.value?.ids[i];
            if (clickedId == null) return;

            goToActionPointsList(clickedId);
        }
    },

    ...props.chartOptions
};

const totalCount = computed(() => (chartData.value != null ? getTotalCountFromChartData(chartData.value) : 0));
const allowMagnify = computed(() => props.showMagnify ?? false);

const isFullscreen = ref(false);

const tableData = computed(() => {
    if (!chartData.value?.labels || !chartData.value.datasets) return [];
    const firstDataset = chartData.value.datasets[0];
    const values = firstDataset?.data ?? [];

    return (chartData.value.labels as string[]).map((label: string, index: number) => ({
        _id: chartData.value?.ids?.[index] ?? index.toString(),
        organization: label,
        amount: values[index] ?? 0
    }));
});

const tableHeaders = [
    { title: 'Vestiging', value: 'organization', sortable: true },
    { title: 'Openstaande actiepunten', value: 'amount', sortable: true }
];

onMounted(async () => {
    isLoading.value = true;
    const result = await actionPointStore.getChartData(organizationId.value, props.isCompleted, props.itemType);

    if (result.status != 200) {
        isLoading.value = false;
        processErrorWithFeedback('Grafiekdata kon niet worden opgehaald.', result);
    }

    chartData.value = result.object;
    isLoading.value = false;
});

function goToActionPointsList(clickedOrgId: string) {
    const routeLocation: RouteLocationRaw = { name: 'actionPoints' };
    filterStore.selectedFilters = {
        status: props.isCompleted ? 'done' : 'ne:done'
    };

    if (props.itemType === 'organization') {
        routeLocation.params = { type: ActionListType.Organization, id: clickedOrgId };
    }

    filterStore.selectedFilters[props.itemType] = clickedOrgId;

    if (props.isCompleted) {
        const gte = `gte:${currentMonthStartDate.getFullYear()}-${currentMonthStartDate.getMonth() + 1}-${currentMonthStartDate.getDate()}`;
        const lt = `lt:${nextMonthStartDate.getFullYear()}-${nextMonthStartDate.getMonth() + 1}-${nextMonthStartDate.getDate()}`;

        const conditions = gte + ',' + lt;

        filterStore.selectedFilters['completedOn'] = props.isCompleted ? conditions : '';
    }

    router.push(routeLocation);
}
</script>

<template>
    <div class="rounded-xl shadow-xl pa-0">
        <div class="d-flex px-4 bg-main-dark justify-center align-center rounded-t-xl">
            <h4 class="text-center my-0 d-inline py-5 px-2 text-white">{{ title }}</h4>
            <v-btn
                v-if="allowMagnify"
                icon="zoom_in"
                color="white"
                variant="tonal"
                size="x-small"
                aria-label="Vergroot grafiek"
                @click="isFullscreen = true"
            />
        </div>
        <div class="rounded-b-xl pa-lg-2 d-flex justify-center align-center" style="height: 17.5em">
            <v-progress-circular v-if="isLoading" color="primary" indeterminate />
            <div v-else>
                <div class="d-flex" style="height: 14em">
                    <div v-if="chartData && totalCount > 0" class="chart-wrapper">
                        <Chart class="chart" :type="type" :data="chartData" :options="options" />
                    </div>
                    <p v-else class="d-flex align-center">{{ emptyMessage }}</p>
                </div>
                <p class="text-center ma-0">Totaal {{ totalCount }}</p>
            </div>
            <v-dialog v-model="isFullscreen" :max-width="!smAndDown ? '60%' : '100%'">
                <v-card class="pa-5">
                    <v-card-title class="d-flex justify-space-between align-center pl-0 mb-4">
                        <strong>{{ title }}</strong>
                        <v-spacer />
                        <v-icon icon="close" class="hover-main rounded-xl" @click="isFullscreen = false" />
                    </v-card-title>
                    <v-card-text class="overflow-y-auto" style="max-height: 80vh">
                        <div class="mb-6" style="height: 55vh">
                            <Chart class="chart" v-if="chartData" :type="type" :data="chartData" :options="options" />
                        </div>
                        <List
                            class="v-col-lg-8"
                            :headers="tableHeaders"
                            :items="tableData"
                            density="compact"
                            entity-name="Item"
                            :itemsPerPage="-1"
                            hide-default-footer
                            @click-table-row="(org) => goToActionPointsList(org._id)"
                        />
                    </v-card-text>
                </v-card>
            </v-dialog>
        </div>
    </div>
</template>

<style scoped>
.chart-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
}

.chart {
    width: 100%;
    height: 100%;
}
</style>
